import styled from "styled-components";
import { CategoryCalcNode } from "./AlternativesEditor";
import ProductList from "./ProductList";
import Checkbox from "../components/Checkbox";
import { ProductOrBrand } from "../model/alternativeTypes";
import { useEffect, useRef, useState } from "react";
import { Button } from "../components/Button";
import { FiCheck, FiChevronDown, FiChevronUp, FiEdit } from "react-icons/fi";
import { Expanding, NameInput } from "./EditorHelpers";
import { productsAreSame } from "../model/selectAlternatives";

interface CategoryEditorProps {
  flyerLocale: string;
  category: CategoryCalcNode;
  onChange: (update: CategoryCalcNode) => void;
}

const CategoryEditor: React.FC<CategoryEditorProps> = ({ flyerLocale, category, onChange }) => {
  const [isExpanded, setExpanded] = useState(false);
  const [isEditingName, setEditingName] = useState(false);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEditingName]);

  const handleProductUpdate = (isAvoid: boolean, update: ProductOrBrand) => {
    let avoid: ProductOrBrand[] = category.avoid.slice()
    let consider: ProductOrBrand[] = category.consider.slice()
    const foundInAvoid = category.avoid.find((p) => productsAreSame(p, update))
    const foundInConsider = category.consider.find((p) => productsAreSame(p, update))
    if (foundInAvoid) {
      if (isAvoid) {
        // replace
        avoid = category.avoid.map((p) => productsAreSame(p, update) ? update : p)
      } else {
        // move from avoid to consider
        consider.push(update)
        avoid = avoid.reduce((acc, item) => {
          if (!productsAreSame(item, update)) {
            acc.push(item);
          }
          return acc;
        }, []);
      }
    } else if (foundInConsider) {
      if (isAvoid) {
        // move from consider to avoid
        avoid.push(update)
        consider = consider.reduce((acc, item) => {
          if (!productsAreSame(item, update)) {
            acc.push(item);
          }
          return acc;
        }, []);
      } else {
        // replace
        consider = category.consider.map((p) => productsAreSame(p, update) ? update : p)
      }
    } else { // not found, add
      if (isAvoid) {
        avoid.push(update)
      } else {
        consider.push(update)
      }
    }
    onChange({
      ...category, 
      avoid: avoid,
      consider: consider
    })
  };

  return (
    <CategoryContainer>
      <HeaderBar>
        {isEditingName ? (<>
          <NameInput
            ref={nameInputRef}
            type="text"
            value={category.name}
            onChange={(e) => onChange({
              ...category,
              name: e.target.value
            })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setEditingName(false);
              }          
            }}
          />
          <Button $borderless onClick={() => setEditingName(false)}>
              <FiCheck size={18} style={{ verticalAlign: 'middle' }} />
          </Button>
        </>) : (<>
          <Checkbox
            id={category.id}
            checked={category.include || false}
            onChange={() => onChange({
              ...category,
              include: !category.include
            })}
            label={category.name}
          />
          <Expanding />
          <Button $borderless $hoverVisible className="hover-icon"  onClick={() => setEditingName(true)}>
              <FiEdit size={18} style={{ verticalAlign: 'middle' }} />
          </Button>
          <Button $borderless onClick={() => setExpanded(!isExpanded)}>
            {isExpanded 
              ? <FiChevronUp size={18} style={{ verticalAlign: 'middle' }} />
              : <FiChevronDown size={18} style={{ verticalAlign: 'middle' }} />}
          </Button>
        </>)}
      </HeaderBar>
      {isExpanded && (
        <>
          <ProductList
            isAvoid={true}
            categoryName={category.id}
            flyerLocale={flyerLocale}
            products={category.avoid}
            onReorder={(ps) => onChange({
              ...category,
              avoid: ps
            })}
            onChange={(p) => handleProductUpdate(true, p)}
          />
          <ProductList
            isAvoid={false}
            categoryName={category.id}
            flyerLocale={flyerLocale}
            products={category.consider}
            onReorder={(ps) => onChange({
              ...category,
              consider: ps
            })}
            onChange={(p) => handleProductUpdate(false, p)}
          />
        </>
      )}
    </CategoryContainer>
  )
}

const CategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
`;

const HeaderBar = styled.div`
  display: flex;
  width: 100%;
  gap: 5px;

  &:hover .hover-icon {
    opacity: 1;
  }
`;



export default CategoryEditor;